import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import {
	BatchEditReport,
	BatchEditService,
	BatchSummary,
	effectiveStatus
} from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from '../../data-model';
import { ProjectService } from '../../project.service';

@Component({
	selector: 'bp-batch-edit-history',
	standalone: true,
	imports: [CommonModule, NgbProgressbarModule],
	templateUrl: './batch-edit-history.component.html',
	styleUrls: ['./batch-edit-history.component.css']
})
export class BatchEditHistoryComponent implements OnInit {
	/** Alle Punkte, deren eingebettete Historie ausgelesen wird. */
	@Input() points: AufnahmePunktDocument[] = [];

	private batchEditService = inject(BatchEditService);
	private modal = inject(NgbActiveModal);
	private ps = inject(ProjectService);

	summaries: BatchSummary[] = [];

	// Undo-State
	runningUndoId: string | null = null;
	progressDone = 0;
	progressTotal = 0;
	currentPointId = '';
	report: BatchEditReport | null = null;

	ngOnInit(): void {
		this.refresh();
	}

	private refresh(): void {
		this.summaries = this.batchEditService.listBatches(this.points, 50);
	}

	formatTimestamp(ts: number): string {
		return new Date(ts).toLocaleString('de-DE');
	}

	/**
	 * Darf der aktuelle User diesen Batch zurückrollen (G9)? Initiator oder
	 * Owner/Manager/Entwickler. Bindet den „Rückgängig"-Button.
	 */
	canUndo(summary: BatchSummary): boolean {
		return this.ps.canIundoBatch(summary);
	}

	async undoBatch(summary: BatchSummary): Promise<void> {
		// G9: Defense-in-depth — fremde Batches sind über canUndo bereits
		// disabled, hier zusätzlich hart abriegeln.
		if (!this.canUndo(summary)) return;

		const affected = this.points.filter(p =>
			(p.batchEditHistory ?? []).some(e => e.batchId === summary.batchId)
		);
		if (affected.length === 0) return;

		this.runningUndoId = summary.batchId;
		this.progressDone = 0;
		this.progressTotal = affected.length;
		this.currentPointId = '';
		this.report = null;

		this.report = await this.batchEditService.undoBatch(summary.batchId, affected, {
			label: `Undo: ${summary.label ?? summary.batchId}`,
			// G10: eingefrorene Punkte uniform überspringen — nur editierbare Stati
			// (Bestand/bearbeitung/abgelehnt) werden zurückgerollt.
			canWritePoint: (p: any) => {
				const s = effectiveStatus(p);
				return s === null || s === 'bearbeitung' || s === 'abgelehnt';
			},
			skipReason: 'seit der Sammelbearbeitung eingefroren (Kontrolle/Prüfung/abgeschlossen)',
			onProgress: p => {
				this.progressDone = p.done;
				this.progressTotal = p.total;
				this.currentPointId = p.pointId;
			}
		});

		this.runningUndoId = null;
		this.refresh();
	}

	isRevert(summary: BatchSummary): boolean {
		return !!summary.revertOfBatchId;
	}

	close(): void {
		this.modal.close(this.report);
	}

	cancel(): void {
		this.modal.dismiss('cancel');
	}
}
